import { Select } from 'antd';
import { snakeCase } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAssetChooserState } from 'components/choosers/AssetChooser/useAssetChooserState';
import { useGetAssetTypesMinimalQuery } from 'types/atlas-graphql';

import { filterOption } from '../../helpers';
import { ClearIcon, SuffixIcon } from '../ComponentChooser.style';

export interface IComponentTypeChooserProps {
  disabled: boolean;
  value?: string[]; // Currently selected items
  onChange: any;
  mode?: 'multiple' | 'tags' | undefined;
  allowedOptions?: string[];
}

export const ComponentTypeChooser: React.FunctionComponent<IComponentTypeChooserProps> = ({
  disabled,
  value,
  onChange,
  mode = 'multiple',
  allowedOptions,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useGetAssetTypesMinimalQuery({
    skip: disabled,
    variables: {
      excludeUninstantiated: true,
    },
  });

  const sortedComponentTypes = useMemo(() => {
    if (data) {
      const newTypes = data?.assetTypes ?? [];
      // We want to display all non-site and non-turbine types.
      const filteredTypes = newTypes.filter(
        t =>
          !['Site', 'Turbine'].includes(t.name) &&
          (!allowedOptions || allowedOptions.includes(t.name))
      );
      return filteredTypes.sort((a, b) => (a?.name < b?.name ? -1 : 1));
    }
  }, [data, allowedOptions]);

  const { handleChange, setVisible, chooserState } = useAssetChooserState({
    onChange,
    value,
  });
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
    setVisible(open);
  };

  return (
    <Select
      id="component-type-chooser"
      placeholder={t('table.selectComponentType', 'Select Component Type...')}
      loading={loading}
      allowClear
      showSearch
      filterOption={filterOption}
      mode={mode}
      maxTagCount={1}
      value={chooserState}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      clearIcon={<ClearIcon />}
      suffixIcon={chooserState ? null : <SuffixIcon />}
      autoClearSearchValue={false}
      searchValue={searchValue}
      onSearch={setSearchValue}
    >
      {sortedComponentTypes?.map(component => (
        <Select.Option
          key={component.name}
          className="component-type-chooser-option"
          value={snakeCase(component.name)}
        >
          {component.name}
        </Select.Option>
      ))}
    </Select>
  );
};
